import { useActorRef, useSelector } from '@xstate/react';
import { useCallback, useEffect, useMemo } from 'react';
import {
  parseISODate,
  toISODate,
  type CalendarViewMode,
} from '~/features/shared/calendar/dateUtils';
import { availabilityCalendarMachine, getVisibleDates } from './availabilityCalendar.machine';
import {
  selectAnchorISO,
  selectBusy,
  selectConflict,
  selectContracts,
  selectContractsError,
  selectDays,
  selectEditorDates,
  selectError,
  selectHasAuthoritativeLoad,
  selectIsSingleDateEditor,
  selectListings,
  selectLoading,
  selectNotice,
  selectRules,
  selectScope,
  selectSelectedDates,
  selectTimezone,
  selectTodayISO,
  selectViewMode,
} from './selectors';
import type {
  AvailabilityScope,
  WritableAvailabilityEntry,
  WritableAvailabilityException,
  WritableAvailabilityRule,
  WritableAvailabilityRulePatch,
  WritableBatchDay,
} from './types';
import { availabilityScopeKey } from './types';

export const useAvailabilityCalendarService = (initialScope: AvailabilityScope) => {
  const machineRef = useActorRef(availabilityCalendarMachine, { input: { initialScope } });
  const anchorISO = useSelector(machineRef, selectAnchorISO);
  const viewMode = useSelector(machineRef, selectViewMode);
  const days = useSelector(machineRef, selectDays);
  const contracts = useSelector(machineRef, selectContracts);
  const contractsError = useSelector(machineRef, selectContractsError);
  const rules = useSelector(machineRef, selectRules);
  const listings = useSelector(machineRef, selectListings);
  const scope = useSelector(machineRef, selectScope);
  const selectedDates = useSelector(machineRef, selectSelectedDates);
  const editorDates = useSelector(machineRef, selectEditorDates);
  const isSingleDateEditor = useSelector(machineRef, selectIsSingleDateEditor);
  const timezone = useSelector(machineRef, selectTimezone);
  const todayISO = useSelector(machineRef, selectTodayISO);
  const error = useSelector(machineRef, selectError);
  const hasAuthoritativeLoad = useSelector(machineRef, selectHasAuthoritativeLoad);
  const conflict = useSelector(machineRef, selectConflict);
  const notice = useSelector(machineRef, selectNotice);
  const loading = useSelector(machineRef, selectLoading);
  const busy = useSelector(machineRef, selectBusy);

  useEffect(() => {
    machineRef.send({ type: 'ON_LOAD' });
  }, [machineRef]);

  useEffect(() => {
    if (
      availabilityScopeKey(machineRef.getSnapshot().context.scope) !==
      availabilityScopeKey(initialScope)
    ) {
      machineRef.send({ type: 'ON_SET_SCOPE', scope: initialScope });
    }
  }, [initialScope, machineRef]);

  const anchor = useMemo(() => parseISODate(anchorISO), [anchorISO]);
  const visibleDates = useMemo(() => getVisibleDates(anchorISO, viewMode), [anchorISO, viewMode]);
  const daysByDate = useMemo(() => new Map(days.map((day) => [day.date, day])), [days]);
  // Only meaningful (and only ever non-null) in single-date mode: a template
  // editor's dates were never all loaded as one day, so it always starts
  // blank rather than pre-filling from any of them.
  const editorDay =
    isSingleDateEditor && editorDates.length === 1
      ? (daysByDate.get(editorDates[0]) ?? null)
      : null;

  const setAnchor = useCallback(
    (nextAnchor: Date) =>
      machineRef.send({ type: 'ON_SET_ANCHOR', anchorISO: toISODate(nextAnchor) }),
    [machineRef]
  );
  const setViewMode = useCallback(
    (nextViewMode: CalendarViewMode) =>
      machineRef.send({ type: 'ON_SET_VIEW', viewMode: nextViewMode }),
    [machineRef]
  );
  const setScope = useCallback(
    (nextScope: AvailabilityScope) => machineRef.send({ type: 'ON_SET_SCOPE', scope: nextScope }),
    [machineRef]
  );
  const toggleDate = useCallback(
    (date: string, shiftKey = false) => machineRef.send({ type: 'ON_TOGGLE_DATE', date, shiftKey }),
    [machineRef]
  );
  const markDates = useCallback(
    (status: 'AVAILABLE' | 'UNAVAILABLE') => machineRef.send({ type: 'ON_BULK_MARK', status }),
    [machineRef]
  );
  const clearDates = useCallback(() => machineRef.send({ type: 'ON_CLEAR_DATES' }), [machineRef]);
  const clearSelection = useCallback(
    () => machineRef.send({ type: 'ON_CLEAR_SELECTION' }),
    [machineRef]
  );
  const clearDay = useCallback(
    (date: string) => machineRef.send({ type: 'ON_CLEAR_DAY', date }),
    [machineRef]
  );
  const saveDay = useCallback(
    (date: string, entries: WritableAvailabilityEntry[]) =>
      machineRef.send({ type: 'ON_SAVE_DAY', date, entries }),
    [machineRef]
  );
  const saveDays = useCallback(
    (daysToSave: WritableBatchDay[]) => machineRef.send({ type: 'ON_SAVE_DAYS', days: daysToSave }),
    [machineRef]
  );
  const openEditor = useCallback(() => machineRef.send({ type: 'ON_OPEN_EDITOR' }), [machineRef]);
  const closeEditor = useCallback(() => machineRef.send({ type: 'ON_CLOSE_EDITOR' }), [machineRef]);
  const saveRule = useCallback(
    (rule: WritableAvailabilityRule | WritableAvailabilityRulePatch, ruleId?: number) =>
      machineRef.send({ type: 'ON_SAVE_RULE', rule, ruleId }),
    [machineRef]
  );
  const deleteRule = useCallback(
    (ruleId: number) => machineRef.send({ type: 'ON_DELETE_RULE', ruleId }),
    [machineRef]
  );
  const saveException = useCallback(
    (ruleId: number, exception: WritableAvailabilityException) =>
      machineRef.send({ type: 'ON_SAVE_EXCEPTION', ruleId, exception }),
    [machineRef]
  );
  const retry = useCallback(() => machineRef.send({ type: 'ON_RETRY' }), [machineRef]);

  return {
    anchor,
    busy,
    clearDates,
    clearDay,
    clearSelection,
    closeEditor,
    conflict,
    contracts,
    contractsError,
    daysByDate,
    deleteRule,
    editorDates,
    editorDay,
    error,
    hasAuthoritativeLoad,
    isSingleDateEditor,
    listings,
    loading,
    markDates,
    openEditor,
    notice,
    retry,
    rules,
    saveDay,
    saveDays,
    saveException,
    saveRule,
    scope,
    selectedDates,
    setAnchor,
    setScope,
    setViewMode,
    timezone,
    todayISO,
    toggleDate,
    viewMode,
    visibleDates,
  };
};
